import { useEffect, useState } from 'react';
import {
  msUntilQueued,
  msUntilStale,
  presentRunStatus,
  type RunPresentation,
  resolveEffectiveRunStatus,
} from './runLifecycle';
import type { AgenticRun, RunStatus } from './types';

/**
 * The run status to render, re-evaluated when the stale window closes.
 *
 * The timer is the whole point. A run abandoned mid-flight — its worker pod dying mid-run, say —
 * keeps returning a byte-identical `running` stub (heartbeat and all — the worker that would refresh
 * it is gone), so react-query's structural sharing hands back the same object and never notifies.
 * Without a scheduled wake-up the page would keep reporting a run that stopped existing, with the
 * button disabled, until something unrelated forced a render.
 *
 * The effect only reads `heartbeatAt`, `generatedAt` and `status` — pulled into locals rather than
 * read off `run` inside the effect body — so its dependency list is genuinely exhaustive rather than
 * narrowed against the linter's advice. Depending on the whole `run` object would re-arm the timer on
 * every poll a stage or progress-note changes, even though neither affects when the clock closes.
 */
export const useEffectiveRunStatus = (
  run: AgenticRun | null | undefined,
): RunStatus | undefined => {
  const [, forceReevaluate] = useState(0);
  const status = run ? resolveEffectiveRunStatus(run, Date.now()) : undefined;
  const heartbeatAt = run?.heartbeatAt;
  const generatedAt = run?.generatedAt;

  useEffect(() => {
    if (status !== 'running' || generatedAt === undefined) return;

    const timer = setTimeout(
      () => forceReevaluate((count) => count + 1),
      msUntilStale({ heartbeatAt, generatedAt }, Date.now()) + 100,
    );
    return () => clearTimeout(timer);
  }, [heartbeatAt, generatedAt, status]);

  return status;
};

/**
 * Its own timer, for the reason `useEffectiveRunStatus` needs one: a run waiting on the queue
 * returns a byte-identical stub, so nothing renders at the `RUN_QUEUED_AFTER_MS` boundary by itself.
 */
export const useRunPresentation = (
  run: AgenticRun | null | undefined,
): RunPresentation | undefined => {
  const status = useEffectiveRunStatus(run);
  const [, forceReevaluate] = useState(0);
  const heartbeatAt = run?.heartbeatAt;
  const generatedAt = run?.generatedAt;

  useEffect(() => {
    if (status !== 'running' || heartbeatAt !== undefined || generatedAt === undefined) return;

    const delay = msUntilQueued({ generatedAt }, Date.now());
    if (delay <= 0) return;

    const timer = setTimeout(() => forceReevaluate((count) => count + 1), delay + 100);
    return () => clearTimeout(timer);
  }, [heartbeatAt, generatedAt, status]);

  return status !== undefined && run ? presentRunStatus(status, run, Date.now()) : status;
};
